import React from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/faq.css';
import { Accordion, Button, Container, Row, Col, InputGroup, FormControl, Form } from 'react-bootstrap';
import axios from 'axios';
import SecurityHeaders from '../../data/SecurityHeaders';
import ENDPOINT from '../../data/ENDPOINT';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import faqGif from './question.gif';
import faqLg from './faq-lg.jpg';
import downGif from './scroll-down.gif';

function FaqPage() {
    const params = useParams();
    const { url } = params;

    const [faqData, setFaqData] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [selectedCategory, setSelectedCategory] = React.useState('General');
    const [error, setError] = React.useState(null);

    // Category list for dropdown (static)
    const categoryList = [
        { value: 'General', name: 'General' },
        { value: 'live-online-courses', name: 'Online Courses' },
        { value: 'offline-courses', name: 'Offline Courses' },
        { value: 'specialization-courses', name: 'Specialization Courses' },
        { value: 'selfpaced-courses', name: 'Selfpaced Courses' },
        { value: 'campus-ambassador-internship', name: 'Campus Ambassador' },
    ];

    // Categories for filter dropdown
    const categories = React.useMemo(() => {
        return [
            { value: 'all', name: 'All Categories' },
            ...categoryList,
        ];
    }, []);

    // Fetch FAQ data
    const getFaqData = async () => {
        const data = new FormData();
        const page = url || selectedCategory || 'General'; 
        data.append('page', page);
        try {
            const response = await axios.post(ENDPOINT + 'get-faq.php', data, SecurityHeaders);
            if (response.data.status === 'success') {
                setFaqData(response.data.data || []);
                setError(null);
            } else {
                setError('Failed to fetch FAQs. Please try again.');
            }
        } catch (error) {
            setError('An error occurred while fetching FAQs.');
            console.error('Error fetching FAQ data:', error);
        }
    };

    React.useEffect(() => {
        getFaqData();
    }, [url, selectedCategory]);

    // Filtered FAQ data
    const filteredFaqs = faqData.filter(item => {
        const matchesSearch =
            (item.title.toLowerCase() || '').includes(search.toLowerCase()) ||
            (item.description.toLowerCase() || '').includes(search.toLowerCase());
        const matchesCategory =
            selectedCategory === 'all' || item.page === selectedCategory || item.category === selectedCategory|| !item.category;
        return matchesSearch && matchesCategory;
    });

    // console.log('Filtered FAQs:', filteredFaqs);

    return (
        <div className="bg-gradient min-vh-100" style={{ background: 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)' }}>
            <div className="py-5 text-center bg-primary text-white shadow-lg rounded-bottom-4">
                <h1 className="fw-bold mb-3 display-4">How can we help?</h1>
                <p className="lead mb-4 fs-5 text-white">Find answers, resources, and support for your journey.</p>
                <form className="d-flex col-xl-6 col-lg-6 col-md-6 col-sm-10 col-10 mx-auto  justify-content-center" onSubmit={e => e.preventDefault()}>
                    <InputGroup className="w-100 shadow-sm">
                        <FormControl
                            name="search"
                            type="text"
                            placeholder="Search FAQs..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="rounded-start-pill px-4 py-3 border-0"
                            aria-label="Search FAQs"
                        />
                        <Button variant="light" className="rounded-end-pill ms-2 px-4 fw-semibold shadow-sm" type="submit">
                            Search
                        </Button>
                    </InputGroup>
                </form>
                {/* Filter by category */}
                <div className="d-flex justify-content-center mt-3">
                    <Form.Select
                        style={{ maxWidth: 250 }}
                        value={selectedCategory}
                        onChange={e => setSelectedCategory(e.target.value)}
                        className="rounded-pill shadow-sm"
                        aria-label="Select FAQ category"
                    >
                        {categories.map(cat => (
                            <option value={cat.value} key={cat.value}>
                                {cat.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            </div>

            <Container className="my-5 py-4 bg-white rounded-4 shadow-lg">
                {error && <div className="alert alert-danger">{error}</div>}
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                        <h2 className="fw-bold mb-0 text-primary">We've got the answers</h2>
                        <img src={faqGif} width="55" height="55" className="ms-2" alt="FAQ icon" />
                    </div>
                    <img src={downGif} width="55" height="55" className="ms-2 d-none d-md-block" alt="Scroll down" />
                </div>
                <Row className="align-items-center">
                    <Col xs={12} md={7}>
                        <Accordion defaultActiveKey="0" flush>
                            {filteredFaqs.length === 0 ? (
                                <div className="text-muted text-center py-5">No FAQs found.</div>
                            ) : (
                                filteredFaqs.map((item, index) => (
                                    <Accordion.Item eventKey={index.toString()} key={index} className="mb-3 border-0 shadow-sm rounded-3">
                                        <Accordion.Header>
                                            <span className="fw-semibold text-dark">{item.title}</span>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <hr className="my-2" />
                                            <p className="mb-0">{item.description}</p>
                                            {item.category && (
                                                <span className="badge bg-secondary mt-2">{item.category}</span>
                                            )}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))
                            )}
                        </Accordion>
                    </Col>
                    <Col xs={12} md={5} className="text-center mt-4 mt-md-0 d-none d-md-block">
                        <img
                            src={faqLg}
                            style={{ width: '80%', maxWidth: 320, borderRadius: '2rem', boxShadow: '0 4px 24px #0001' }}
                            alt="FAQ illustration"
                        />
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default FaqPage;